// web/src/auth/AuthProvider.tsx — מצב הכניסה לכל האפליקציה (D-245).
// 🔑 מי אני — תמיד מהשרת (/me), ⛔ לא מהאסימון: התפקיד והאם צוות נקבעים במסד (is_staff = app.is_admin_staff).
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/auth-js";      // 🆕 27.09 — auth-js בלבד (lib/supabase.ts)
import { supabase } from "../lib/supabase";
import { core, toApiError, type ApiError } from "../api/client";
import type { components } from "../api/gen/core";

export type Me = components["schemas"]["Me"];

type AuthState =
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "error"; error: ApiError }
  | { status: "signedIn"; session: Session; me: Me };

type AuthApi = {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<string | null>;
  /** 🆕 28.09 — AC-10 קישור כניסה במייל. null = נשלח; אחרת — משפט לבעלת החשבון. */
  sendLink: (email: string) => Promise<string | null>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthApi | null>(null);

async function loadMe(session: Session): Promise<AuthState> {
  const { data, error, response } = await core.GET("/me");
  if (data) return { status: "signedIn", session, me: data };
  if (response.status === 401) {
    await supabase.auth.signOut();          // אסימון שהשרת לא מקבל — ⛔ נשארים "מחוברות" בלי גישה
    return { status: "signedOut" };
  }
  return { status: "error", error: toApiError(response.status, error) };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let alive = true;
    const apply = async (session: Session | null) => {
      const next = session ? await loadMe(session) : ({ status: "signedOut" } as const);
      if (alive) setState(next);
    };
    supabase.auth.getSession().then(({ data }) => apply(data.session));
    // 🔑 רק כניסה / יציאה משנות את הזהות. ⛔ חידוש אסימון (TOKEN_REFRESHED) — לא טוענים את /me מחדש.
    // ⚠️ setTimeout: קריאה ל-Supabase מתוך ההתראה עצמה עלולה להיתקע (הבקשה לשרת מבקשת את האסימון) — דוחים סבב.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        setTimeout(() => void apply(session), 0);
      }
    });
    return () => { alive = false; sub.subscription.unsubscribe(); };
  }, []);

  const api: AuthApi = {
    state,
    async signIn(email, password) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error) return null;
      return error.status === 400 ? "האימייל או הסיסמה לא נכונים." : "לא הצלחנו להתחבר כרגע. אפשר לנסות שוב.";
    },
    async sendLink(email) {
      // 🔑 shouldCreateUser: false — ⛔ חשבון חדש מכל מייל שמוקלד (חשבון נוצר מרכישה / מעתידה).
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false, emailRedirectTo: window.location.origin } });
      if (!error) return null;
      if (error.status === 429) return "כבר נשלח קישור לפני רגע. אפשר לשלוח שוב בעוד דקה.";
      if (error.status === 422 || error.status === 400 || /signup|not allowed|not found/i.test(error.message))
        return "לא מצאנו חשבון עם המייל הזה. אם רכשת עם מייל אחר — נסי אותו, או כתבי לעתידה.";
      return "לא הצלחנו לשלוח קישור כרגע. אפשר לנסות שוב.";
    },
    async signOut() { await supabase.auth.signOut(); },
  };
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth מחוץ ל-AuthProvider");
  return v;
}
